/**
 * AE-CA Chat API Service for Account Executive
 * Handles all REST API calls for Account Executive ↔ Company Admin messaging
 */
import { BASE_URL } from '../components/BaseUrl';

class AeCaChatApi {
  constructor(token) {
    this.token = token;
    this.apiBaseUrl = BASE_URL;
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
    };
  }

  /**
   * Get all AE-CA chat threads for the authenticated Account Executive
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} Response with threads and pagination
   */
  async getThreads(page = 1, limit = 20) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get list of Company Admins the Account Executive can chat with
   * @param {string} search - Search query for name/email
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 500)
   * @returns {Promise<Object>} Response with eligible users
   */
  async getEligibleUsers(search = '', page = 1, limit = 500) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/eligible-users?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Create a new AE-CA chat thread or get existing thread with a Company Admin
   * @param {number} receiverId - Company Admin user ID
   * @param {string} receiverRole - Receiver role (default: 'company_admin')
   * @returns {Promise<Object>} Response with thread data
   */
  async createOrGetThread(receiverId, receiverRole = 'company_admin') {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ receiverId, receiverRole }),
      }
    );
    return response.json();
  }

  /**
   * Get existing thread ID with a specific user
   * @param {number} userId - Company Admin user ID
   * @returns {Promise<Object>} Response with thread data
   */
  async getThreadByUserId(userId) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/thread-by-user/${userId}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get detailed information about a specific AE-CA thread
   * @param {number} threadId - Thread ID
   * @returns {Promise<Object>} Response with thread details
   */
  async getThreadDetails(threadId) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads/${threadId}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get messages in a specific AE-CA thread
   * @param {number} threadId - Thread ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 7)
   * @returns {Promise<Object>} Response with messages and pagination
   */
  async getThreadMessages(threadId, page = 1, limit = 7) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads/${threadId}/messages?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Send a text message or file in an AE-CA thread
   * @param {number} threadId - Thread ID
   * @param {string} content - Message content
   * @param {string} messageType - Message type: 'text', 'image', 'file', 'system'
   * @param {string|null} fileUrl - File URL (optional)
   * @param {string|null} fileName - File name (optional)
   * @param {number|null} fileSize - File size in bytes (optional)
   * @param {string|null} mimeType - MIME type (optional)
   * @returns {Promise<Object>} Response with sent message data
   */
  async sendMessage(threadId, content, messageType = 'text', fileUrl = null, fileName = null, fileSize = null, mimeType = null) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads/${threadId}/messages`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ 
          content, 
          messageType,
          fileUrl,
          fileName,
          fileSize,
          mimeType
        }),
      }
    );
    return response.json();
  }

  /**
   * Mark all messages in an AE-CA thread as read (or up to a specific message)
   * @param {number} threadId - Thread ID
   * @param {number|null} upToMessageId - Optional message ID to mark up to
   * @returns {Promise<Object>} Response with marked count
   */
  async markAsRead(threadId, upToMessageId = null) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads/${threadId}/read`,
      {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ upToMessageId }),
      }
    );
    return response.json();
  }

  /**
   * Mark all messages in an AE-CA thread as read when user opens chat
   * @param {number} threadId - Thread ID
   * @returns {Promise<Object>} Response with marked count
   */
  async markAllAsRead(threadId) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/threads/${threadId}/read-all`,
      {
        method: 'PUT',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get total unread AE-CA message count for Account Executive
   * @returns {Promise<Object>} Response with total unread count
   */
  async getUnreadCount() {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/unread-count`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Get detailed unread message counts (total and per-thread)
   * @returns {Promise<Object>} Response with detailed unread counts
   */
  async getDetailedUnreadCounts() {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/unread-counts/detailed`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Soft delete an AE-CA message (only sender can delete)
   * @param {number} messageId - Message ID
   * @returns {Promise<Object>} Response with success message
   */
  async deleteMessage(messageId) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/messages/${messageId}`,
      {
        method: 'DELETE',
        headers: this.getHeaders(),
      }
    );
    return response.json();
  }

  /**
   * Edit an AE-CA message content (only sender can edit)
   * @param {number} messageId - Message ID
   * @param {string} content - Updated message content
   * @returns {Promise<Object>} Response with updated message data
   */
  async editMessage(messageId, content) {
    const response = await fetch(
      `${this.apiBaseUrl}/ae-ca-chat/messages/${messageId}`,
      {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({ content }),
      }
    );
    return response.json();
  }
}

export default AeCaChatApi;

